import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import axios from 'src/utils/axios';
import helpers from 'src/helpers';
import HelpCircleWithContainer from 'src/components/HelpCircle/HelpCircleWithContainer';

/* *************************************************************************
 * FormEntityChooser
 * Entity picker shown after an Event/Group Smart Form is selected in the
 * form-URL insert picker. Two modes:
 *   - perRecipient: emit an id-LESS token ([fe:code]/[fg:code]) resolved to
 *     each recipient's own event/group from send context. Only offered where
 *     the send can resolve one (entity-context screens).
 *   - specific: emit an id-FUL token ([fe:code:ID]) tied to one chosen
 *     event/group. Pre-selected to the ambient entity when one exists.
 * Option list mirrors the Booking/Joining modal's grouping: open entities
 * first (newest first), closed beneath, grouped under headers.
 *************************************************************************** */
function FormEntityChooser({
  entityKind,
  perRecipientAvailable = false,
  ambientEntityId = null,
  // Render menus inside the host DOM instead of a portal. Needed ONLY when
  // the host has its own outside-click closer (SelectDynamicJoinFormUrl's
  // custom dropdown) that would treat a portaled menu as an outside click.
  // Plain Dialog hosts (chat modal) keep the portal — an in-DOM listbox gets
  // clipped by the Dialog paper's overflow.
  disablePortal = false,
  // Caption flavor for per-recipient mode: 'selection' on the Event/Group
  // List multi-select screens (entities were explicitly selected); 'send'
  // (default) everywhere else (type-level SA/rule/welcome editors, where the
  // entity is whichever one the message is eventually sent for).
  perRecipientScope = 'send',
  value,
  onChange,
}) {
  const [entityOptions, setEntityOptions] = useState(null);
  const [loading, setLoading] = useState(false);

  const isEvent = entityKind === 'Event';
  const recipientLabel = isEvent
    ? 'The event each guest is in'
    : 'The group each contact is in';
  const isSendScope = perRecipientScope !== 'selection';
  const recipientCaption = (() => {
    if (!isSendScope) {
      return isEvent
        ? "Each guest's link automatically uses whichever of the selected events they're in."
        : "Each contact's link automatically uses whichever of the selected groups they're in.";
    }
    if (perRecipientScope === 'submission') {
      return isEvent
        ? "Each guest's link automatically uses the event the form was submitted for."
        : "Each contact's link automatically uses the group the form was submitted for.";
    }
    return isEvent
      ? "Each guest's link automatically uses the event this message is sent for."
      : "Each contact's link automatically uses the group this message is sent for.";
  })();
  const recipientInfo = isEvent
    ? 'Choose "A specific event" instead only if you want this link tied to one '
      + 'particular event everywhere this message is used.'
    : 'Choose "A specific group" instead only if you want this link tied to one '
      + 'particular group everywhere this message is used.';

  useEffect(() => {
    let cancelled = false;
    const fetchOptions = async () => {
      setLoading(true);
      try {
        const resp = isEvent
          ? await axios.get('/api/parties/list')
          // Production max is 172 active groups for one user. The endpoint
          // clamps a query pageSize at 500 (CON-1675), so 2000 resolves to
          // 500 — still well above any real account, and complete without
          // pagination plumbing.
          : await axios.get('/api/groups', { params: { pageSize: 2000 } });
        if (cancelled) return;
        const rows = isEvent
          ? helpers.transform.toArray(resp?.data?.parties)
          : helpers.transform.toArray(resp?.data?.groups);
        const toOption = (row) => ({
          value: row.id,
          label: (isEvent ? row.partyName : row.groupName) || `${entityKind} ${row.id}`,
          closed: !!row.closed,
          startDate: isEvent && row.partyStartDateTime
            ? moment(row.partyStartDateTime).format('MM/DD/YY')
            : null,
        });
        const open = rows.filter((r) => !r.closed).map(toOption).reverse();
        const closed = rows.filter((r) => r.closed).map(toOption).reverse();
        setEntityOptions([...open, ...closed]);
      } catch (e) {
        if (!cancelled) setEntityOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOptions();
    return () => { cancelled = true; };
  }, [entityKind]);

  const selectedOption = useMemo(() => (
    (entityOptions || []).find((o) => o.value === value?.entityId) || null
  ), [entityOptions, value?.entityId]);

  const showModeSelect = perRecipientAvailable;

  return (
    <Box sx={{ marginTop: 1, marginBottom: 1 }}>
      <Typography variant="caption" sx={{ display: 'block', marginBottom: 0.5 }}>
        {isEvent
          ? 'Which event should submissions go to?'
          : 'Which group should submissions go to?'}
      </Typography>
      {showModeSelect && (
        <Select
          size="small"
          fullWidth
          MenuProps={{ disablePortal }}
          value={value?.mode || 'perRecipient'}
          onChange={(e) => onChange({
            mode: e.target.value,
            entityId: e.target.value === 'specific'
              ? (value?.entityId ?? ambientEntityId ?? null)
              : null,
          })}
          sx={{ marginBottom: 1 }}
        >
          <MenuItem value="perRecipient">{recipientLabel}</MenuItem>
          <MenuItem value="specific">{`A specific ${isEvent ? 'event' : 'group'}`}</MenuItem>
        </Select>
      )}
      {showModeSelect && (value?.mode || 'perRecipient') === 'perRecipient' && (
        <Box sx={{ marginBottom: 1 }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {recipientCaption}
          </Typography>
          {isSendScope && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                <b>This is usually what you want.</b>
              </Typography>
              <HelpCircleWithContainer
                positionX="afterText"
                positionY="center"
                isShowInfoV2
                showInfo={recipientInfo}
              />
            </Box>
          )}
        </Box>
      )}
      {(value?.mode === 'specific' || !showModeSelect) && (
        <Autocomplete
          size="small"
          disablePortal={disablePortal}
          // With disablePortal the listbox inherits ancestor text-align
          // (centered in some SA editors) — pin options + group headers left.
          ListboxProps={{ style: { textAlign: 'left' } }}
          loading={loading}
          options={entityOptions || []}
          groupBy={(o) => (o.closed
            ? `Closed ${isEvent ? 'Events' : 'Groups'}`
            : `Open ${isEvent ? 'Events' : 'Groups'}`)}
          // Field shows the plain name; the start date renders as a second
          // line in the option list (CP's contact-list pattern: name first,
          // details on a purple second line).
          getOptionLabel={(o) => o.label}
          renderOption={(props, o) => (
            <li {...props} key={o.value}>
              <Box>
                <Typography variant="body2">{o.label}</Typography>
                {o.startDate && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'primary.main' }}
                  >
                    {`Starts ${o.startDate}`}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          value={selectedOption}
          // The chosen option rides along as a second argument for hosts that
          // need the entity's name (the Smart Form QR title); others ignore it.
          onChange={(_e, option) => onChange({
            mode: 'specific',
            entityId: option?.value ?? null,
          }, option)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={`Choose ${isEvent ? 'an event' : 'a group'}...`}
              variant="outlined"
            />
          )}
        />
      )}
    </Box>
  );
}

FormEntityChooser.propTypes = {
  entityKind: PropTypes.oneOf(['Event', 'Group']).isRequired,
  perRecipientAvailable: PropTypes.bool,
  ambientEntityId: PropTypes.number,
  disablePortal: PropTypes.bool,
  perRecipientScope: PropTypes.oneOf(['selection', 'send', 'submission']),
  value: PropTypes.shape({
    mode: PropTypes.oneOf(['perRecipient', 'specific']),
    entityId: PropTypes.number,
  }),
  onChange: PropTypes.func.isRequired,
};

export default FormEntityChooser;
